import { AddCircle } from '@material-ui/icons'
import { cloneElement } from 'react'
import {
  BooleanInput,
  Button,
  CheckboxGroupInput,
  ExportButton,
  Filter,
  FunctionField,
  sanitizeListRestProps,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
  useRedirect,
} from 'react-admin'
import { usePageTitle } from 'shared/components/PageTitle/PageTitle'
import { SOURCE_NAME_CHOICES } from 'shared/helpers/constants'

import { BrokerReferenceInput } from 'components/Common/Brokers'
import {
  BundleChoices,
  CarrierMCChoices,
  LABEL_DROPOFF_DATE,
  LABEL_ORIGIN_TO_DESTINATION,
  LABEL_PICKUP_DATE,
  STATUS_DETAIL_CHOICES,
} from '../../helpers/constants'
import { getProductName, getTeamAndProductName, getUserFullName } from '../../helpers/utils'
import { CustomReferenceField } from '../Common/CustomReferenceFields'
import { CTDatagrid } from '../CTDatagrid'
import { LocalizedStopDateTimeField, OriginDestinationField } from '../Fields'
import CTList from '../List'

const JobFilter = (props: any) => (
  <Filter {...props}>
    <TextInput label="Search" source="search" alwaysOn />
    <SelectInput label="Source" source="load__source" choices={SOURCE_NAME_CHOICES} />
    <CheckboxGroupInput label="Detail" source="status_detail" choices={STATUS_DETAIL_CHOICES} />
    <BooleanInput label="Payment in dispute" source="payment_in_dispute" />
    <BooleanInput label="Hazmat detected" source="hazmat_detected" />
    <SelectInput label="Product Type" source="product_subscription__product_name" choices={BundleChoices} />
    <SelectInput
      label="Carrier"
      source="product_subscription__carrier__mc_number"
      choices={CarrierMCChoices}
    />
    <BrokerReferenceInput
      label="Broker / Shipper"
      source="load__broker__id"
      reference="brokers"
      showQuickCreate={false}
      filterToQuery={(searchText: any) => ({ name: searchText })}
      enableGetChoices={({ name }: any) => (name ? name.length >= 3 : false)}
      perPage={5000}
    />
    <BooleanInput label="Is Shipper" source="load__broker__is_shipper" />
    <BooleanInput label="Auto-approved" source="is_auto_approved" />
  </Filter>
)

const CreateJobWithRateConButton = () => {
  const redirect = useRedirect()
  const createJob = () => redirect('/external-book')
  return (
    <Button label="Create Job with Rate Con" basePath="jobs" onClick={createJob}>
      <AddCircle />
    </Button>
  )
}

const JobActions = ({
  currentSort,
  className,
  resource,
  exporter, // you can hide ExportButton if exporter = (null || false)
  filterValues,
  permanentFilter,
  maxResults,
  total,
  showFilter,
  displayedFilters,
  filters,
  ...rest
}: any) => (
  <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
    <CreateJobWithRateConButton />
    {filters &&
      cloneElement(filters, {
        resource,
        showFilter,
        displayedFilters,
        filterValues,
        context: 'button',
      })}
    <ExportButton
      disabled={total === 0}
      resource={resource}
      sort={currentSort}
      filterValues={filterValues}
      exporter={exporter}
      maxResults={maxResults}
    />
  </TopToolbar>
)

const JobList = (props: any) => {
  usePageTitle('Jobs')

  return (
    <CTList bulkActionButtons={false} filters={<JobFilter />} actions={<JobActions />} {...props}>
      <CTDatagrid optimized>
        <TextField source="human_id" label="Job Id" />
        <FunctionField
          label="Product Subscription"
          render={(record: any) => {
            return getProductName(record?.product_subscription?.bundle_name, true)
          }}
        ></FunctionField>
        <TextField source="product_subscription.carrier.name" label="Carrier" />
        <TextField source="load.source" label="Source" />
        <TextField source="load.broker.name" label="Broker" />
        <TextField source="load.source_human_id" label="Broker load ID" />
        <CustomReferenceField
          source="assigned_to"
          sortable={false}
          label="Assigned Driver"
          baseUrl="/users"
          representationFunction={getUserFullName}
        />
        <CustomReferenceField
          source="team"
          label="Team (Product)"
          sortable={false}
          baseUrl="/teams"
          representationFunction={getTeamAndProductName}
        />
        <FunctionField
          label={LABEL_ORIGIN_TO_DESTINATION}
          render={(record: any) => {
            return <OriginDestinationField record={record.load} />
          }}
        />
        <FunctionField
          label={LABEL_PICKUP_DATE}
          sortBy="load__origin_pickup_date"
          render={(record: any) => {
            return <LocalizedStopDateTimeField record={record.load} source="origin_pickup_date" />
          }}
        />
        <FunctionField
          label={LABEL_DROPOFF_DATE}
          sortBy="load__dest_delivery_date"
          render={(record: any) => {
            return <LocalizedStopDateTimeField record={record.load} source="dest_delivery_date" />
          }}
        />
        <TextField source="status" />
        <TextField source="status_detail" />
      </CTDatagrid>
    </CTList>
  )
}

export default JobList
